import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { PageHeader, Panel, LoadingState, EmptyState, ErrorState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { ArrowLeftRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function Deposits() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ classification: "external_deposit", amount: "", note: "", method: "Bank Transfer", reference: "", status: "completed", source_account: "", destination_account: "" });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["transactions"],
    queryFn: async () => (await api.get("/transactions")).data,
  });

  const mutation = useMutation({
    mutationFn: async (payload) => (await api.post("/transactions", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
      setForm({ classification: "external_deposit", amount: "", note: "", method: "Bank Transfer", reference: "", status: "completed", source_account: "", destination_account: "" });
      toast.success("Transaction recorded (DEMO)");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const needsAccounts = ["internal_transfer", "profit_reallocation"].includes(form.classification);
  const statusOptions = form.classification === "personal_withdrawal"
    ? ["pending", "failed", "cancelled"]
    : ["completed", "pending", "failed", "cancelled"];

  const onClassChange = (v) => {
    const next = { ...form, classification: v };
    if (v === "personal_withdrawal" && next.status === "completed") next.status = "pending";
    if (!["internal_transfer", "profit_reallocation"].includes(v)) { next.source_account = ""; next.destination_account = ""; }
    setForm(next);
  };

  const submit = () => {
    const amt = Number(form.amount);
    if (form.amount === "" || !Number.isFinite(amt) || amt <= 0) return toast.error("Enter a valid finite positive amount");
    if (needsAccounts) {
      if (!form.source_account || !form.destination_account) return toast.error("Select source and destination accounts");
      if (form.source_account === form.destination_account) return toast.error("Source and destination accounts must be different");
    }
    mutation.mutate({ ...form, amount: amt });
  };

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState label="Failed to load transactions" onRetry={() => refetch()} />;

  const items = data?.items ?? [];
  const summary = [
    ["External Deposits", fmtMoney(data.total_deposits), "text-emerald-400"],
    ["Withdrawals (Completed)", fmtMoney(data.total_withdrawals_completed), "text-rose-400"],
    ["Withdrawals (Pending)", fmtMoney(data.total_withdrawals_pending), "text-amber-400"],
    ["Net External Flow", fmtMoney(data.net_external_flow), Number(data.net_external_flow) >= 0 ? "text-emerald-400" : "text-rose-400"],
  ];

  const typeBadge = { deposit: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", withdrawal: "text-rose-400 bg-rose-500/10 border-rose-500/30", transfer: "text-blue-400 bg-blue-500/10 border-blue-500/30", adjustment: "text-slate-400 bg-slate-500/10 border-slate-600/40" };
  const statusBadge = { completed: "text-emerald-400", pending: "text-amber-400", failed: "text-rose-400", cancelled: "text-slate-500" };

  return (
    <div>
      <PageHeader
        title="Deposits & Withdrawals"
        subtitle="Capital movements · withdrawals are never counted as trading losses"
        icon={ArrowLeftRight}
        testId="deposits-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-btn" className="bg-blue-600 hover:bg-blue-500 text-white h-9 text-xs">
                <Plus className="w-4 h-4 mr-1" /> New Transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
              <DialogHeader><DialogTitle>Record Transaction (DEMO)</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Classification</Label>
                  <Select value={form.classification} onValueChange={onClassChange}>
                    <SelectTrigger data-testid="txn-type" className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
                      <SelectItem value="external_deposit">External Deposit</SelectItem>
                      <SelectItem value="personal_withdrawal">Personal Withdrawal</SelectItem>
                      <SelectItem value="internal_transfer">Internal Transfer</SelectItem>
                      <SelectItem value="profit_reallocation">Profit Reallocation</SelectItem>
                      <SelectItem value="fee_charge">Fee or Charge</SelectItem>
                      <SelectItem value="adjustment">Adjustment</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">Internal transfers & profit reallocations do not change consolidated capital.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Amount (INR)</Label>
                    <Input data-testid="txn-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" placeholder="Enter amount" />
                  </div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="txn-status" className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
                        {statusOptions.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {form.classification === "personal_withdrawal" && (
                      <p className="text-[10px] text-amber-400/80 mt-1" data-testid="withdrawal-status-note">Withdrawals stay 'pending' until authoritative settlement confirmation.</p>
                    )}
                  </div>
                </div>

                {needsAccounts && (
                  <div className="grid grid-cols-2 gap-3" data-testid="account-mapping">
                    <div>
                      <Label className="text-xs">Source Account</Label>
                      <Select value={form.source_account} onValueChange={(v) => setForm({ ...form, source_account: v })}>
                        <SelectTrigger data-testid="txn-source-account" className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]"><SelectValue placeholder="Select source" /></SelectTrigger>
                        <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
                          {(data?.accounts || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Destination Account</Label>
                      <Select value={form.destination_account} onValueChange={(v) => setForm({ ...form, destination_account: v })}>
                        <SelectTrigger data-testid="txn-destination-account" className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]"><SelectValue placeholder="Select destination" /></SelectTrigger>
                        <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
                          {(data?.accounts || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {form.classification === "profit_reallocation" && (
                  <p className="text-[10px] text-amber-400/80" data-testid="reallocation-evidence-note">Profit reallocation is limited to realized P&amp;L evidenced in the ledger. A 'completed' reallocation beyond evidence is rejected — use 'pending' with trade evidence.</p>
                )}
                <div>
                  <Label className="text-xs">Method</Label>
                  <Input data-testid="txn-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
                </div>
                <div>
                  <Label className="text-xs">Note</Label>
                  <Input data-testid="txn-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 bg-[var(--bg-canvas)] border-[var(--border-subtle)]" placeholder="Optional" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="txn-submit" onClick={submit} disabled={mutation.isPending} className="bg-blue-600 hover:bg-blue-500 text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {summary.map(([k, v, tone]) => (
          <div key={k} className="rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] p-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{k}</div>
            <div className={cn("font-mono-num text-xl font-bold", tone)}>{v}</div>
          </div>
        ))}
      </div>

      <Panel title="Transaction History" testId="transactions-panel" actions={<DemoBadge />}>
        {items.length === 0 ? (
          <EmptyState label="No transactions recorded" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="transactions-table">
              <thead className="bg-[var(--surface)]">
                <tr className="border-b border-[var(--border-subtle)] text-slate-400">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Classification</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-left font-medium">Method</th>
                  <th className="px-3 py-2 text-left font-medium">Reference</th>
                  <th className="px-3 py-2 text-left font-medium">Note</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} data-testid={`txn-row-${t.reference}`} className="border-b border-[var(--border-subtle)] hover:bg-[var(--card-hover)]">
                    <td className="px-3 py-2.5 font-mono-num text-slate-400">{new Date(t.date).toLocaleDateString("en-IN")}</td>
                    <td className="px-3 py-2.5"><span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded border uppercase", typeBadge[t.type])}>{t.classification_label || t.type}</span></td>
                    <td className="px-3 py-2.5 text-right font-mono-num text-slate-200">{fmtMoney(t.amount)}</td>
                    <td className="px-3 py-2.5 text-slate-400">{t.method}</td>
                    <td className="px-3 py-2.5 font-mono-num text-slate-500">{t.reference}</td>
                    <td className="px-3 py-2.5 text-slate-400 max-w-[200px] truncate">{t.note || "—"}</td>
                    <td className={cn("px-3 py-2.5 font-mono-num capitalize", statusBadge[t.status])}>{t.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
